<template>
  <v-container fluid>
    <v-row
      v-if="loading"
      justify="center"
      align="center"
      style="min-height: 85vh"
    >
      <v-progress-circular indeterminate color="primary" size="64" />
    </v-row>

    <template v-else>
      <v-row v-if="sortedSchedules.length > 0" dense>
        <v-col
          v-for="card in sortedSchedules"
          :key="card.id"
          :cols="12"
          md="6"
          lg="4"
          xl="3"
        >
          <v-card
            :class="[{ 'card-disabled': !card.active, card: card.active }]"
            dark
            style="display: flex; flex-direction: column; height: 100%;"
          >
            <v-list-item three-line>
              <v-list-item-content>
                <div
                  class="disableText text-overline mb-4 d-flex justify-space-between"
                >
                  {{ formatCardHeadline(card) }}
                  <span class="disableText">{{
                    remainingTimes[card.id] || "Calculating..."
                  }}</span>
                </div>
                <v-list-item-title class="disableText text-h5 mb-1">
                  {{ card.title }}
                </v-list-item-title>
                <v-list-item-subtitle class="disableText">
                  {{ formattedTemplates(card) || "Loading templates..." }}
                </v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>
            <v-dialog v-model="dialog" max-width="600px">
              <v-card>
                <v-card-title class="headline"
                  >Are you sure you want to cancel this schedule?</v-card-title
                >
                <v-card-actions>
                  <v-spacer></v-spacer>
                  <v-btn color="blue darken-1" text @click="dialog = false"
                    >Cancel</v-btn
                  >
                  <v-btn color="blue darken-1" text @click="cancelConfirm"
                    >Confirm</v-btn
                  >
                  <v-spacer></v-spacer>
                </v-card-actions>
              </v-card>
            </v-dialog>

            <v-card-actions>
              <v-btn
                :class="{ disableText: !card.active }"
                plain
                @click="cancel(card)"
              >
                Cancel
              </v-btn>

              <v-spacer></v-spacer>

              <v-btn
                :class="{ disableText: !card.active }"
                icon
                @click="edit(card)"
              >
                Edit
              </v-btn>

              <v-btn
                :class="{ disableText: !card.active }"
                icon
                @click="sell(card)"
              >
                Sell
              </v-btn>

              <v-btn
                :class="{ disableText: !card.active }"
                icon
                @click="call(card)"
              >
                Call
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-col>
      </v-row>
      <v-row v-else>
        <v-col cols="12" class="text-center scheduleMain">
          <v-card class="py-12" flat>
            <img
              src=".././../../src/assets/noschedule.svg"
              alt="no schedule found"
              loading="lazy"
            />
            <v-btn class="scheduleButton" block @click="CreateSchedule"
              >Schedule Game</v-btn
            >
          </v-card>
        </v-col>
      </v-row>
    </template>
  </v-container>
</template>

<script>
import SockJS from "sockjs-client";
import Stomp from "webstomp-client";
import { mapActions, mapGetters } from "vuex";
import { calculateRemainingTime } from "../../utils/calculateRemainingTime";
import router from "@/router";

export default {
  name: "Schedules",
  components: {},
  computed: {
    ...mapGetters(["getScheduleList"]),
    formattedScheduleList() {
      return this.getScheduleList.map((card) => ({
        ...card,
        formattedTemplates: this.formatCardText(card),
      }));
    },
    sortedScheduleList() {
      const sortSchedules = (schedules) => {
        return schedules.sort((a, b) => {
          if (a.date !== b.date) {
            return new Date(b.date) - new Date(a.date);
          }

          const timeA = this.calculateTimeValue(a.date, a.time);
          const timeB = this.calculateTimeValue(b.date, b.time);
          return timeA - timeB;
        });
      };
      const activeSchedules = sortSchedules(
        this.getScheduleList.filter((card) => card.active)
      );
      const inactiveSchedules = sortSchedules(
        this.getScheduleList.filter((card) => !card.active)
      );
      return [...activeSchedules, ...inactiveSchedules];
    },
  },
  methods: {
    ...mapActions([
      "fetchUpcoming",
      "fetchStatus",
      "setCurrentPage",
      "cancelSchedule",
      "startSchedule",
    ]),
    CreateSchedule() {
      this.$router.push("/scheduler/create");
    },
    calculateTimeValue(date, time) {
      const formattedTime = `${String(time[0]).padStart(2, "0")}:${String(
        time[1]
      ).padStart(2, "0")}`;
      const scheduleTime = new Date(`${date}T${formattedTime}Z`);
      return scheduleTime.getTime();
    },

    formattedTemplates(card) {
      if (!card?.selectedTemplates) return "";
      const hasUUID = card.selectedTemplates.some((template) =>
        /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
          template
        )
      );
      if (hasUUID) return "";
      return card.gameOrder.map((g) => g.productTemplateDto?.title).join(", ");
    },
    async connectToSockets() {
      this.getScheduleList.forEach(async (schedule) => {
        if (
          schedule.gameMode === "PASSIVE" &&
          schedule.type === "FIXED_ODDS_BINGO" &&
          schedule.active === true &&
          schedule.autoStart
        ) {
          if (!this.activeSockets[schedule.id]) {
            console.log(
              `✅ Connecting to STOMP for schedule ${schedule.id} - gameMode: ${schedule.gameMode}, type: ${schedule.type}, autoStart: ${schedule.autoStart}`
            );
            await this.initializeSocket(schedule);
            console.log("activeSockets", this.activeSockets.length);
          } else {
            console.log(
              `❌ Not connecting to STOMP for schedule ${schedule.id} - gameMode: ${schedule.gameMode}, type: ${schedule.type}, autoStart: ${schedule.autoStart}`
            );
            console.log("activeSockets", this.activeSockets);
          }
        }
      });
    },
    async fetchAndSortSchedules() {
      await this.fetchUpcoming();
      this.sortedSchedules = this.sortSchedules(this.getScheduleList);
      this.updateRemainingTimes();
      this.connectToSocketsOnce();
      this.loading = false;
    },
    sortSchedules(schedules) {
      const sortFn = (a, b) => {
        if (a.date !== b.date) {
          return new Date(b.date) - new Date(a.date);
        }
        const timeA = this.calculateTimeValue(a.date, a.time);
        const timeB = this.calculateTimeValue(b.date, b.time);
        return timeA - timeB;
      };
      const active = schedules.filter((c) => c.active).sort(sortFn);
      const inactive = schedules.filter((c) => !c.active).sort(sortFn);
      return [...active, ...inactive];
    },
    updateRemainingTimes() {
      this.sortedSchedules.forEach((card) => {
        const newTime = calculateRemainingTime(card.date, card.time);
        if (this.remainingTimes[card.id] !== newTime) {
          this.$set(this.remainingTimes, card.id, newTime);
        }
      });
    },
    connectToSocketsOnce() {
      if (this._socketsConnected) return;
      this._socketsConnected = true;
      this.sortedSchedules.forEach(async (schedule) => {
        if (
          schedule.gameMode === "PASSIVE" &&
          schedule.type === "FIXED_ODDS_BINGO" &&
          schedule.active === true &&
          schedule.autoStart
        ) {
          if (!this.activeSockets[schedule.id]) {
            await this.initializeSocket(schedule);
          }
        }
      });
    },
    async initializeSocket(schedule) {
      const socket = new SockJS(process.env.VUE_APP_BASE_URL + "play");
      const stompClient = Stomp.over(socket);
      stompClient.connect({}, () => {
        const topic = `/topic/${schedule.id}/startSubscribingNewScreens`;
        const subscription = stompClient.subscribe(topic, (message) => {
          this.parseNewScreensSignal(JSON.parse(message.body));
        });
        this.activeSockets[schedule.id] = stompClient;
        this.socketSubscriptions[schedule.id] = subscription;
      });
      socket.onclose = () => {
        delete this.activeSockets[schedule.id];
        if (this.socketSubscriptions[schedule.id]) {
          this.socketSubscriptions[schedule.id].unsubscribe();
          delete this.socketSubscriptions[schedule.id];
        }
      };
    },
    parseNewScreensSignal(signal) {
      this.startSchedule(signal.id);
      setTimeout(() => {
        const path = `/play/game/viewFixedOddBingo/${signal.id}`;
        window.open(path, `bingo-tab-${signal.id}`);
        router.push("/play/game/fixed_odds_bingo");
      }, 2000);
    },

    formatCardText(card) {
      if (!card?.selectedTemplates) return "";
      return card.selectedTemplates.join(", ");
    },
    formatCardHeadline(card) {
      const hours = String(card.time[0]).padStart(2, "0");
      const minutes = String(card.time[1]).padStart(2, "0");
      const localDateTime = new Date(`${card.date}T${hours}:${minutes}:00Z`);
      const localHours = localDateTime
        .getHours()
        .toString()
        .padStart(2, "0");
      const localMinutes = localDateTime
        .getMinutes()
        .toString()
        .padStart(2, "0");
      return `${card.date} (${localHours}:${localMinutes})`;
    },
    getImageSrc(card) {
      return "data:image/jpeg;base64," + card.gameOrder[0].logo;
    },
    startTimer() {
      setInterval(() => {
        this.updateRemainingTimes();
      }, 1000);
    },
    edit(card) {
      console.log(card);
      let path = "products" + "/" + this.getSchedule(card).type + "/" + card.id;
      this.$router.push({ path: path });
    },
    cancel(card) {
      this.selectedItem = card;
      this.dialog = true;
    },
    cancelConfirm() {
      this.cancelSchedule(this.selectedItem.id);
      this.dialog = false;
    },
    getSchedule(card) {
      return this.getScheduleList.find((schedule) => schedule.id == card.id);
    },
    sell(card) {
      console.log(card);
      let path = "sales" + "/" + card.id;
      this.$router.push({ path: path });
    },
    call(card) {
      this.fetchStatus(card.id);
      this.$router.push({ path: `/start/${card.id}` });
    },
  },
  mounted() {
    this.startTimer();
    this.setCurrentPage("Schedules");
  },
  created() {
    this.fetchAndSortSchedules();
  },
  beforeDestroy() {
    Object.values(this.socketSubscriptions).forEach((sub) => {
      if (sub && typeof sub.unsubscribe === 'function') sub.unsubscribe();
    });
    Object.values(this.activeSockets).forEach((stompClient) => {
      if (stompClient && stompClient.connected) {
        stompClient.disconnect();
      }
    });
    this.activeSockets = {};
    this.socketSubscriptions = {};
  },
  data: () => ({
    dialog: false,
    selectedItem: null,
    remainingTimes: {},
    activeSockets: {},
    loading: true,
    sortedSchedules: [], // Store sorted schedules here
    socketSubscriptions: {}, // Track subscriptions for cleanup
  }),
};
</script>
<style scoped>
.scheduleMain {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 85vh;
}

.scheduleButton {
  padding: 20px 24px !important;
  justify-content: center;
  align-items: center;
  gap: 10px;
  border-radius: 12px !important;
  background-color: #323887 !important;
  color: white !important;
}
</style>
